import { SceneService } from '../../services/SceneService';
import { Scene } from '../models/layout';
import { EditorChangeType } from '../types';
import { AbstractCommand } from './AbstractCommand';

import type { IScene } from '../models/layout/types';
import type { VideoEditor } from '../VideoEditor';

/**
 * UpdateSceneCommand
 * Updates properties of an existing scene
 */
export class UpdateSceneCommand extends AbstractCommand {
  private sceneId: string;
  private updates: Partial<IScene>;
  private previousScene: Scene | null = null;
  private service: SceneService;

  constructor(editor: VideoEditor, sceneId: string, updates: Partial<IScene>) {
    super(editor);
    this.sceneId = sceneId;
    this.updates = updates;
    this.service = new SceneService();
  }

  getName(): string {
    return 'UpdateSceneCommand';
  }

  execute(): boolean {
    // Store previous state for undo
    const scene = this.service.getScene(this.editor.timeline, this.sceneId);

    if (!scene) {
      return false;
    }

    this.previousScene = scene;

    // Create updated scene by merging
    const updatedScene = Scene.fromJSON({
      ...scene.toJSON(),
      ...this.updates,
      id: this.sceneId,
    });

    const updated = this.service.updateScene(
      this.editor.timeline,
      this.sceneId,
      updatedScene,
    );

    if (!updated) {
      return false;
    }

    this.editor.markDirty();
    this.editor.notifyObservers(EditorChangeType.SCENE_UPDATED, {
      sceneId: this.sceneId,
      updates: this.updates,
    });

    return true;
  }

  undo(): boolean {
    if (!this.previousScene) {
      return false;
    }

    // Restore previous scene
    const updated = this.service.updateScene(
      this.editor.timeline,
      this.sceneId,
      this.previousScene,
    );

    if (!updated) {
      return false;
    }

    this.editor.markDirty();
    this.editor.notifyObservers(EditorChangeType.SCENE_UPDATED, {
      sceneId: this.sceneId,
      updates: this.previousScene.toJSON(),
    });
    this.editor.notifyObservers(EditorChangeType.TIMELINE_CHANGED);

    return true;
  }

  redo(): boolean {
    return this.execute();
  }

  getDescription(): string {
    const updateKeys = Object.keys(this.updates).join(', ');
    return `Update scene (${updateKeys})`;
  }

  shouldCreateUndoRedoEntry(): boolean {
    return true;
  }
}
